import { Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import { SellerApi } from '../api/seller.api';

import { AppConfigService } from '@app/modules/app-load/services/app-config.service';
import { Seller } from '../models/portal.models';

@Injectable({
  providedIn: 'root'
})
export class SellerService {
  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {}

  get endPoint(): string {
    return (
      this.appConfigService.getConfig().apiGatewayEndpoint +
      '/api/rest/v1/seller'
    );
  }

  private readonly apiRoutes = {
    originationConfig: () => `${this.endPoint}/config`,
    purchaseConfig: (): string => `${this.endPoint}/config`,
    salesPerson: (): string => `${this.endPoint}/salesperson`,
    rpaStatements: (): string => `${this.endPoint}/rpa/statements`,
    rpaStatement: (): string => `${this.endPoint}/rpa/statement`,
    rpaTransactions: (): string => `${this.endPoint}/rpa/transactions`,
    sellers: (): string => `${this.endPoint}/sellers`
  };

  getOriginationConfig(
    sellerId: number
  ): Observable<SellerApi.originationConfig.Response> {
    const httpParams = new HttpParams().append('sellerId', sellerId.toString());
    return this.http.get<SellerApi.originationConfig.Response>(
      this.apiRoutes.originationConfig(),
      { params: httpParams }
    );
  }

  getPurchaseConfig(
    request: SellerApi.purchaseConfig.Request
  ): Observable<SellerApi.purchaseConfig.Response> {
    const httpParams = new HttpParams()
      .append('sellerId', request.sellerId.toString())
      .append('cardProgrammeCd', request.cardProgrammeCd);
    return this.http.get<SellerApi.purchaseConfig.Response>(
      this.apiRoutes.purchaseConfig(),
      { params: httpParams }
    );
  }

  getSalesPerson(): Observable<SellerApi.salesPerson.Response> {
    return this.http.get<SellerApi.salesPerson.Response>(
      this.apiRoutes.salesPerson()
    );
  }

  sellerIsAdmin(roles: string): boolean {
    return roles === 'SADM' || roles === 'GADM' || roles === 'SU';
  }

  getRpaStatements(
    request: SellerApi.Rpa.Statements.Request
  ): Observable<SellerApi.Rpa.Statements.Response[]> {
    const params = new HttpParams()
      .append('sellerId', request.sellerId.toString())
      .append('fromDate', request.fromDate)
      .append('toDate', request.toDate);
    return this.http.get<SellerApi.Rpa.Statements.Response[]>(
      this.apiRoutes.rpaStatements(),
      { params }
    );
  }

  getRpaStatement(request: SellerApi.Rpa.Statement.Request): Observable<Blob> {
    const params = new HttpParams()
      .append('sellerId', request.sellerId.toString())
      .append('documentId', request.documentId);
    const options = {
      params,
      headers: new HttpHeaders().append('Content-Type', 'application/pdf'),
      responseType: 'blob' as 'json' // Open bug in angular https://github.com/angular/angular/issues/18586
    };
    return this.http.get<Blob>(this.apiRoutes.rpaStatement(), options);
  }

  getRpaTransactions(
    request: SellerApi.Rpa.Statements.Request
  ): Observable<Blob> {
    let params = new HttpParams()
      .append('sellerId', request.sellerId.toString())
      .append('fromDate', request.fromDate)
      .append('toDate', request.toDate);

    if (request.headOffice) {
      params = params.append('headOffice', request.headOffice);
    }

    const options = {
      params,
      headers: new HttpHeaders().append(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      responseType: 'blob' as 'json' // Open bug in angular https://github.com/angular/angular/issues/18586
    };
    return this.http.get<Blob>(this.apiRoutes.rpaTransactions(), options);
  }

  getSellers(sellerGroupId?: number, name?: string): Observable<Seller[]> {
    let params = new HttpParams();
    if (sellerGroupId ) {
      params = params.append(
        'sellerGroupId',
        sellerGroupId.toString()
      );
    }
    if (name){
      params = params.append(
        'name',
        name
      );
    }
    if(params.keys().length > 0){
      return this.http.get<Seller[]>(this.apiRoutes.sellers(), { params });
    } else {
      return this.http.get<Seller[]>(this.apiRoutes.sellers());
    }

  }
}
